import React, { useState, useEffect } from 'react';
import { MdOutlineClose } from 'react-icons/md';
import theme from '../config/theme';

export type SnackBarType = 'success' | 'error' | 'info' | 'warning';
export type SnackBarPosition = 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right' | 'inline';

export interface SnackBarProps {
  message: string;
  type?: SnackBarType;
  duration?: number; // in milliseconds, 0 for indefinite
  onClose?: () => void;
  show?: boolean;
  position?: SnackBarPosition;
}

const SnackBar: React.FC<SnackBarProps> = ({
  message,
  type = 'success',
  duration = 5000,
  onClose,
  show = true,
  position = 'bottom-center'
}) => {
  const [isVisible, setIsVisible] = useState(show);

  useEffect(() => {
    setIsVisible(show);
  }, [show]);

  useEffect(() => {
    if (isVisible && duration > 0) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        if (onClose) {
          onClose();
        }
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, onClose]);

  const handleClose = () => {
    setIsVisible(false);
    if (onClose) {
      onClose();
    }
  };

  if (!isVisible) return null;

  // Get colors based on type
  const getColors = (type: SnackBarType) => {
    switch (type) {
      case 'success':
        return {
          bg: theme.color.successSurface,
          textColor: theme.color.success,
          border: theme.color.success,
        };
      case 'error':
        return {
          bg: theme.color.error + '10',
          textColor: theme.color.error,
          border: theme.color.error,
        };
      case 'info':
        return {
          bg: theme.color.gray500 + '10',
          textColor: theme.color.gray500,
          border: theme.color.gray500,
        };
      case 'warning':
        return {
          bg: theme.brandColor.yellow + '10',
          textColor: theme.brandColor.yellow,
          border: theme.brandColor.yellow,
        };
      default:
        return {
          bg: theme.color.gray500 + '10',
          textColor: theme.color.gray500,
          border: theme.color.gray500,
        };
    }
  };

  const colors = getColors(type);

  // Get position classes
  const getPositionClasses = (position: SnackBarPosition) => {
    if (position === 'inline') {
      return 'relative'; // For use inside a stacking container
    } else if (position === 'top-left') {
      return 'fixed top-4 left-4';
    } else if (position === 'top-center') {
      return 'fixed top-4 left-1/2 transform -translate-x-1/2';
    } else if (position === 'top-right') {
      return 'fixed top-2 right-4';
    } else if (position === 'bottom-left') {
      return 'fixed bottom-4 left-4';
    } else if (position === 'bottom-center') {
      return 'fixed bottom-4 left-1/2 transform -translate-x-1/2';
    } else if (position === 'bottom-right') {
      return 'fixed bottom-4 right-4';
    } else {
      return 'fixed bottom-4 left-1/2 transform -translate-x-1/2';
    }
  };

  const positionClasses = getPositionClasses(position);

  const marginClass = position === 'inline' ? '' : 'mt-20';

  return (
    <div
      className={`${positionClasses}  ${marginClass} px-4 py-2 rounded-lg shadow-lg border flex items-center justify-between space-x-4 min-w-80 max-w-md animate-slide-up`}
      style={{ backgroundColor: '#fff', borderColor: colors.border, zIndex: 9999 }}
      role="alert"
    >
      {/* Message */}
      <p className={`text-md flex-1`} style={{ color: colors.textColor }}>
        {message}
      </p>

      {/* Close Button */}
      <button
        onClick={handleClose}
        className="flex-shrink-0  rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-current transition-colors"
        style={{ color: colors.textColor }}
        aria-label="Close notification"
      >
        {React.createElement(MdOutlineClose as any, {
          className: "h-5 w-5"
        })}
      </button>
    </div>
  );
};

export default SnackBar;
